import React, { useState, useEffect, useCallback } from 'react';

// Text-free, full-bleed photographic hero that cross-fades through a set of
// Corfu photos. A small white + red progress indicator shows the active slide
// and the switch between them. An SEO heading is kept visually hidden.
const SLIDES = [
  { src: '/images/backgrounds/porto-timoni-beach.webp', alt: 'Porto Timoni twin beaches, Corfu' },
  { src: '/images/backgrounds/coastal-road-aerial.webp', alt: 'Mercedes-Benz V-Class on a Corfu coastal road' },
  { src: '/images/backgrounds/corfu-old-fortress.webp', alt: 'The Old Fortress of Corfu from the sea' },
  { src: '/images/backgrounds/corfu-town-port.webp', alt: 'Corfu Town and its harbour' },
  { src: '/images/backgrounds/corfu-cove-beach.webp', alt: 'A turquoise hidden cove on the Corfu coast' },
  { src: '/images/backgrounds/corfu-coastline-aerial.webp', alt: 'Aerial view of the Corfu coastline at golden hour' },
];

const SLIDE_MS = 6000;

const HeroSection = ({ imagesLoaded }) => {
  const [active, setActive] = useState(0);

  // Advance the carousel; re-arms whenever the active slide changes so manual
  // selection also resets the timer.
  useEffect(() => {
    const id = setTimeout(() => setActive((a) => (a + 1) % SLIDES.length), SLIDE_MS);
    return () => clearTimeout(id);
  }, [active]);

  const goTo = useCallback((i) => setActive(i), []);

  return (
    <section className="relative h-screen overflow-hidden bg-[#faf7f6]">
      {/* Cross-fading full-bleed photos (natural, unfiltered) */}
      {SLIDES.map((slide, i) => (
        <div
          key={slide.src}
          role="img"
          aria-label={slide.alt}
          className="absolute inset-x-0 bottom-0 top-[114px] sm:top-[118px] lg:top-[134px] bg-cover bg-center transition-opacity duration-[1200ms] ease-in-out"
          style={{
            backgroundImage: `url('${slide.src}')`,
            opacity: i === active ? 1 : 0,
          }}
        />
      ))}
      {!imagesLoaded && <div className="absolute inset-0 bg-[#faf7f6]" />}

      {/* Semantic H1 for SEO, visually hidden (the hero is intentionally text-free) */}
      <h1 className="sr-only">
        Lasmari Chauffeurs: Premium Corfu Airport Transfers and Private Chauffeur Service
      </h1>

      {/* White + red slide indicator (shows the active photo and the switch) */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2.5">
        {SLIDES.map((slide, i) => (
          <button
            key={slide.src}
            type="button"
            onClick={() => goTo(i)}
            aria-label={`Show photo ${i + 1} of ${SLIDES.length}`}
            aria-current={i === active}
            className="relative h-[3px] w-9 sm:w-12 rounded-full bg-white/35 overflow-hidden cursor-pointer transition-colors hover:bg-white/55"
            style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.35)' }}
          >
            {i === active && (
              <span
                key={active}
                className="absolute inset-y-0 left-0 w-full rounded-full"
                style={{
                  backgroundColor: '#ba1609',
                  transformOrigin: 'left',
                  animation: `heroProgressBar ${SLIDE_MS}ms linear forwards`,
                }}
              />
            )}
          </button>
        ))}
      </div>
    </section>
  );
};

export default HeroSection;
